import React from 'react';
import { useNavigate } from 'react-router-dom';

/* ------------------------------------------------------------------ */
/*  Site budget data                                                   */
/* ------------------------------------------------------------------ */
const SITES = [
  {
    name: 'UCLA',
    budget: 1_571_540,
    actual: 324_800,
    enrolled: 3,
    planned: 20,
    status: 'On Track' as const,
    statusColor: '#48bb78',
    warning: '',
  },
  {
    name: 'UCSF',
    budget: 1_285_750,
    actual: 298_200,
    enrolled: 3,
    planned: 15,
    status: 'Watch' as const,
    statusColor: '#ecc94b',
    warning: 'Higher than expected',
  },
  {
    name: 'Johns Hopkins',
    budget: 1_222_000,
    actual: 224_200,
    enrolled: 3,
    planned: 15,
    status: 'On Track' as const,
    statusColor: '#48bb78',
    warning: '',
  },
];

const TOTAL_BUDGET = 4_079_290;
const TOTAL_ACTUAL = 847_200;

/* ------------------------------------------------------------------ */
/*  Alerts                                                             */
/* ------------------------------------------------------------------ */
const ALERTS = [
  {
    type: 'warning' as const,
    icon: '\u26A0\uFE0F',
    title: 'sp-004 rejected sample',
    detail: 'Repeat PK draw required at UCSF',
    cost: 'Estimated additional cost: $485',
    impact: 'Impact: +0.01% to budget',
    action: '',
  },
  {
    type: 'warning' as const,
    icon: '\u26A0\uFE0F',
    title: 'SYN-005 Grade 4 AE \u2014 hospitalization',
    detail: 'Additional monitoring visits likely',
    cost: 'Estimated budget impact: +$3,200',
    impact: '',
    action: 'Review with sponsor',
  },
  {
    type: 'info' as const,
    icon: '\u2139\uFE0F',
    title: 'Amendment 1 issued 2/1/2026',
    detail: 'Re-consent visits not yet budgeted',
    cost: 'Estimated cost: 6 patients \u00D7 $250 = $1,500',
    impact: '',
    action: 'Update budget',
  },
];

/* ------------------------------------------------------------------ */
/*  Quick actions                                                      */
/* ------------------------------------------------------------------ */
const QUICK_ACTIONS = [
  { label: 'Import Protocol',  route: '/protocol-setup' },
  { label: 'Budget Calculator', route: '/budget' },
  { label: 'View SOA Grid',    route: '/protocol-setup' },
  { label: 'Export Report',     route: '/budget' },
];

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
function fmt(n: number): string {
  return '$' + n.toLocaleString('en-US');
}

/* ================================================================== */
/*  Component                                                          */
/* ================================================================== */
export default function HomeBudget() {
  const navigate = useNavigate();
  const pct = ((TOTAL_ACTUAL / TOTAL_BUDGET) * 100).toFixed(1);

  return (
    <div style={s.container}>
      {/* ── Header ────────────────────────────────────────────── */}
      <div style={s.headerRow}>
        <div>
          <h1 style={s.title}>Budget Coordinator</h1>
          <p style={s.trialId}>DEMO-001 &middot; Sym004 Phase 1b/2a</p>
          <p style={s.version}>Protocol version: v4.0 &mdash; Sym004 + FOLFIRI</p>
        </div>
        <button style={s.roleBtn} onClick={() => navigate('/role-select')}>
          Switch Role
        </button>
      </div>

      {/* ── Budget Overview ───────────────────────────────────── */}
      <h2 style={s.sectionTitle}>Trial Budget Overview</h2>

      <div style={s.summaryCard}>
        <div style={s.summaryGrid}>
          <div>
            <span style={s.sumLabel}>Total budget</span>
            <span style={s.sumBig}>{fmt(TOTAL_BUDGET)}</span>
          </div>
          <div>
            <span style={s.sumLabel}>Actual to date</span>
            <span style={s.sumBig}>{fmt(TOTAL_ACTUAL)}</span>
          </div>
          <div>
            <span style={s.sumLabel}>Remaining</span>
            <span style={s.sumBig}>{fmt(TOTAL_BUDGET - TOTAL_ACTUAL)}</span>
          </div>
        </div>
        <div style={s.progressOuter}>
          <div style={{ ...s.progressInner, width: `${pct}%` }} />
        </div>
        <span style={s.progressLabel}>{pct}% spent &mdash; <span style={{ color: '#48bb78' }}>On Track</span></span>
      </div>

      {/* site cards */}
      <div style={s.siteGrid}>
        {SITES.map((site) => {
          const sitePct = ((site.actual / site.budget) * 100).toFixed(1);
          return (
            <div key={site.name} style={{ ...s.siteCard, borderTopColor: site.statusColor }}>
              <div style={s.siteHeader}>
                <span style={s.siteName}>{site.name}</span>
                <span style={{ ...s.statusBadge, backgroundColor: site.statusColor + '22', color: site.statusColor, border: `1px solid ${site.statusColor}` }}>
                  {site.status}
                </span>
              </div>
              <div style={s.siteMetrics}>
                <div style={s.metricRow}>
                  <span style={s.metricLabel}>Budget</span>
                  <span style={s.metricValue}>{fmt(site.budget)}</span>
                </div>
                <div style={s.metricRow}>
                  <span style={s.metricLabel}>Actual</span>
                  <span style={s.metricValue}>{fmt(site.actual)}</span>
                </div>
                <div style={s.progressOuter}>
                  <div style={{ ...s.progressInner, width: `${sitePct}%`, backgroundColor: site.statusColor }} />
                </div>
                <span style={{ fontSize: 11, color: '#718096' }}>{sitePct}%</span>
                <div style={s.metricRow}>
                  <span style={s.metricLabel}>Patients</span>
                  <span style={s.metricValue}>{site.enrolled} enrolled of {site.planned} planned</span>
                </div>
              </div>
              {site.warning && (
                <div style={s.warningBox}>{'\u26A0\uFE0F'} {site.warning}</div>
              )}
              <button style={{ ...s.cardBtn, color: site.statusColor }} onClick={() => navigate('/budget')}>
                {'\u2192'} View {site.name} Budget
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Active Protocols ──────────────────────────────────── */}
      <h2 style={s.sectionTitle}>Active Protocols</h2>

      <div style={s.protocolCard}>
        <div style={s.protoHeader}>
          <span style={s.protoTitle}>Sym004-09</span>
          <span style={{ ...s.statusBadge, backgroundColor: '#22543d', color: '#48bb78', border: '1px solid #48bb78' }}>Active</span>
        </div>
        <p style={s.protoSub}>Sym004 + FOLFIRI in mCRC</p>
        <div style={s.protoGrid}>
          <div style={s.metricRow}><span style={s.metricLabel}>Sponsor</span><span style={s.metricValue}>Symphogen A/S</span></div>
          <div style={s.metricRow}><span style={s.metricLabel}>Phase</span><span style={s.metricValue}>Phase 1b/2a</span></div>
          <div style={s.metricRow}><span style={s.metricLabel}>Version</span><span style={s.metricValue}>4.0 / 22-Feb-2017</span></div>
          <div style={s.metricRow}>
            <span style={s.metricLabel}>SOA grid</span>
            <span style={{ ...s.miniBadge, backgroundColor: '#22543d', color: '#48bb78' }}>Imported</span>
          </div>
          <div style={s.metricRow}>
            <span style={s.metricLabel}>CPT mapping</span>
            <span style={{ ...s.miniBadge, backgroundColor: '#22543d', color: '#48bb78' }}>Complete</span>
          </div>
          <div style={s.metricRow}><span style={s.metricLabel}>Total budget</span><span style={s.metricValue}>{fmt(TOTAL_BUDGET)}</span></div>
        </div>
        <button style={{ ...s.cardBtn, color: '#63b3ed', marginTop: 14 }} onClick={() => navigate('/budget')}>
          {'\u2192'} View Full Budget
        </button>
      </div>

      {/* ── Budget Alerts ─────────────────────────────────────── */}
      <h2 style={s.sectionTitle}>Budget Alerts</h2>
      <div style={s.alertList}>
        {ALERTS.map((a, i) => (
          <div key={i} style={{ ...s.alertCard, borderLeftColor: a.type === 'warning' ? '#ed8936' : '#4299e1' }}>
            <div style={s.alertHeader}>
              <span style={{ fontSize: 14 }}>{a.icon}</span>
              <span style={s.alertTitle}>{a.title}</span>
            </div>
            <p style={s.alertDetail}>{a.detail}</p>
            <p style={{ ...s.alertDetail, color: '#ecc94b', fontWeight: 600 }}>{a.cost}</p>
            {a.impact && <p style={{ ...s.alertDetail, color: '#718096' }}>{a.impact}</p>}
            {a.action && <p style={{ ...s.alertDetail, color: '#ed8936', fontWeight: 600 }}>Action: {a.action}</p>}
          </div>
        ))}
      </div>

      {/* ── Quick Actions ─────────────────────────────────────── */}
      <h2 style={s.sectionTitle}>Quick Actions</h2>
      <div style={s.quickRow}>
        {QUICK_ACTIONS.map((qa, i) => (
          <button key={i} style={s.quickBtn} onClick={() => navigate(qa.route)}>
            {qa.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Styles                                                             */
/* ================================================================== */
const s: Record<string, React.CSSProperties> = {
  container: { padding: 32, maxWidth: 960 },

  /* header */
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', margin: 0 },
  trialId: { fontSize: 14, color: '#63b3ed', marginTop: 4 },
  version: { fontSize: 13, color: '#718096', marginTop: 2 },
  roleBtn: {
    padding: '8px 16px', borderRadius: 6, border: '1px solid #2d3748',
    backgroundColor: '#171923', color: '#a0aec0', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },

  /* section */
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#cbd5e0', marginBottom: 12, marginTop: 32, textTransform: 'uppercase' as const, letterSpacing: 0.5 },

  /* summary card */
  summaryCard: {
    backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 8,
    padding: '20px 24px', marginBottom: 16,
  },
  summaryGrid: { display: 'flex', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap' as const, gap: 16 },
  sumLabel: { display: 'block', fontSize: 12, color: '#a0aec0', marginBottom: 2 },
  sumBig: { fontSize: 22, fontWeight: 700, color: '#e2e8f0' },
  progressOuter: { height: 6, backgroundColor: '#2d3748', borderRadius: 3, overflow: 'hidden' as const },
  progressInner: { height: '100%', borderRadius: 3, backgroundColor: '#48bb78', transition: 'width 0.3s' },
  progressLabel: { fontSize: 12, color: '#a0aec0', marginTop: 6, display: 'block' },

  /* site cards */
  siteGrid: { display: 'flex', gap: 16, flexWrap: 'wrap' as const },
  siteCard: {
    flex: '1 1 280px', maxWidth: 320, backgroundColor: '#1a202c',
    border: '1px solid #2d3748', borderTop: '3px solid', borderRadius: 8, padding: '16px 18px',
  },
  siteHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  siteName: { fontSize: 16, fontWeight: 700, color: '#e2e8f0' },
  statusBadge: { fontSize: 10, fontWeight: 700, padding: '2px 10px', borderRadius: 12, letterSpacing: 0.5 },
  siteMetrics: { display: 'flex', flexDirection: 'column' as const, gap: 6 },
  metricRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  metricLabel: { fontSize: 12, color: '#a0aec0' },
  metricValue: { fontSize: 13, fontWeight: 600, color: '#e2e8f0' },
  warningBox: {
    marginTop: 10, padding: '6px 10px', borderRadius: 6,
    backgroundColor: '#744210', color: '#ecc94b', fontSize: 11, fontWeight: 600,
  },
  cardBtn: {
    background: 'none', border: 'none', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', padding: 0, marginTop: 12, transition: 'opacity 0.15s',
  },

  /* protocol card */
  protocolCard: {
    backgroundColor: '#1a202c', border: '1px solid #2d3748', borderLeft: '4px solid #63b3ed',
    borderRadius: 8, padding: '16px 20px',
  },
  protoHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  protoTitle: { fontSize: 16, fontWeight: 700, color: '#e2e8f0' },
  protoSub: { fontSize: 13, color: '#a0aec0', margin: '0 0 12px 0' },
  protoGrid: { display: 'flex', flexDirection: 'column' as const, gap: 6 },
  miniBadge: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12 },

  /* alerts */
  alertList: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  alertCard: {
    backgroundColor: '#1a202c', border: '1px solid #2d3748', borderLeft: '4px solid',
    borderRadius: 8, padding: '10px 16px',
  },
  alertHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
  alertTitle: { fontSize: 13, fontWeight: 600, color: '#e2e8f0' },
  alertDetail: { fontSize: 12, color: '#a0aec0', margin: '0 0 2px 0' },

  /* quick actions */
  quickRow: { display: 'flex', gap: 12, flexWrap: 'wrap' as const },
  quickBtn: {
    padding: '10px 20px', borderRadius: 8, border: '1px solid #2d3748',
    backgroundColor: '#1a202c', color: '#9f7aea', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },
};
